import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  FaArrowLeft,
  FaShieldAlt,
  FaBolt,
  FaLeaf,
  FaCheck,
  FaIndustry,
  FaCogs,
  FaUsers,
  FaGlobe,
  FaTemperatureHigh,
  FaMobileAlt,
  FaBatteryFull,
  FaHome,
  FaSolarPanel,
  FaBuilding,
  FaWarehouse,
  FaSun,
  FaChartLine,
  FaChargingStation,
  FaRecycle,
  FaTools,
  FaFlask,
  FaAward
} from 'react-icons/fa';

/* ============================================================
   IMAGE IMPORTS
============================================================ */

import ashrafiaLogo from '../logos/Asrafiaenergy/Ashrafiaenergylogo.png';

import energyHome from '../logos/Asrafiaenergy/Ashrafiaenergy1.png';
import battery1 from '../logos/Asrafiaenergy/Ashrafiaenergy2.png';
import battery2 from '../logos/Asrafiaenergy/Ashrafiaenergy3.png';
import battery3 from '../logos/Asrafiaenergy/Ashrafiaenergy4.png';

import homesImage from '../logos/Asrafiaenergy/Ashrafiaenergy5.png';
import solarSystemsImage from '../logos/Asrafiaenergy/Ashrafiaenergy6.png';
import commercialImage from '../logos/Asrafiaenergy/Ashrafiaenergy7.png';
import industrialImage from '../logos/Asrafiaenergy/Ashrafiaenergy3.png';

import factoryImage from '../logos/Asrafiaenergy/Ashrafiaenergy2.png';


export default function AshrafiaEnergy() {

  const navigate = useNavigate();

  const revealRefs = useRef([]);

  useEffect(() => {

    const elements = revealRefs.current;

    const observer = new IntersectionObserver(
      (entries) => {

        entries.forEach((entry) => {

          if (entry.isIntersecting) {
            entry.target.classList.add('energy-visible');
          }

        });

      },
      {
        threshold: 0.12
      }
    );

    elements.forEach((element) => {

      if (element) {
        observer.observe(element);
      }

    });

    return () => observer.disconnect();

  }, []);


  const addReveal = (element) => {

    if (
      element &&
      !revealRefs.current.includes(element)
    ) {

      revealRefs.current.push(element);

    }

  };


  return (

    <div className="energy-page">

      {/* =====================================================
          TOP NAVIGATION
      ====================================================== */}

      <div className="energy-top-nav">

        <button
          className="energy-back-btn"
          onClick={() => navigate('/')}
        >

          <FaArrowLeft />

          <span>
            Back to All Sectors
          </span>

        </button>

        <div className="energy-nav-title">
          ASHRAFIA ENERGY
        </div>

      </div>


      {/* =====================================================
          HERO
      ====================================================== */}

      <section className="energy-hero">

        <div className="energy-hero-background">

          <img
            src={energyHome}
            alt="Ashrafia Energy Solar Home"
          />

          <div className="energy-hero-overlay" />

        </div>


        <div className="energy-hero-content">

          {/* LOGO */}

          <div className="energy-logo-area">

            <img
              src={ashrafiaLogo}
              alt="Ashrafia Energy"
              className="energy-logo"
            />

            <div className="energy-logo-line">

              <span />

              <strong>
                ENERGY
              </strong>

              <span />

            </div>

            <p>
              POWERING A SUSTAINABLE FUTURE
            </p>

          </div>


          {/* HERO TEXT */}

          <div
            ref={addReveal}
            className="energy-hero-text energy-reveal"
          >

            <div className="energy-small-label">
              ASHRAFIA ENERGY
            </div>

            <h1>
              ADVANCED
              <br />
              ENERGY STORAGE.
            </h1>

            <h2>
              BUILT FOR TOMORROW.
            </h2>

            <p>
              Ashrafia Energy is the Group's dedicated solar
              battery manufacturing division, specialized in
              high-performance Lithium energy storage solutions.
            </p>

            <p>
              Our Solar Lithium Batteries are engineered for
              safety, efficiency, and long life — delivering
              reliable power for homes, businesses, and industries.
            </p>


            <div className="energy-feature-mini-grid">

              <div className="energy-feature-mini">

                <FaShieldAlt />

                <span>
                  SAFE &amp; RELIABLE
                </span>

              </div>


              <div className="energy-feature-mini">

                <FaBolt />

                <span>
                  LONG LIFE PERFORMANCE
                </span>

              </div>


              <div className="energy-feature-mini">

                <FaBatteryFull />

                <span>
                  HIGH ENERGY EFFICIENCY
                </span>

              </div>


              <div className="energy-feature-mini">

                <FaLeaf />

                <span>
                  CLEAN ENERGY SOLUTION
                </span>

              </div>

            </div>

          </div>


          {/* HERO PRODUCTS */}

          <div
            ref={addReveal}
            className="energy-hero-products energy-reveal"
          >

            <div className="energy-house-image">

              <img
                src={energyHome}
                alt="Solar powered modern home"
              />

            </div>


            <div className="energy-battery-showcase">

              <div className="energy-battery-card">

                <img
                  src={battery1}
                  alt="Ashrafia lithium battery"
                />

              </div>


              <div className="energy-battery-card energy-battery-main">

                <img
                  src={battery2}
                  alt="Ashrafia Energy battery"
                />

              </div>


              <div className="energy-battery-card">

                <img
                  src={battery3}
                  alt="Ashrafia industrial battery"
                />

              </div>

            </div>

          </div>

        </div>


        {/* TECHNOLOGY STRIP */}

        <div className="energy-tech-strip">

          <div>

            <strong>
              100% SOLAR LITHIUM TECHNOLOGY
            </strong>

            <span>
              No Tubular. Only Advanced Lithium Performance.
            </span>

          </div>

        </div>

      </section>


      {/* =====================================================
          MAIN CONTENT
      ====================================================== */}

      <main className="energy-content">


        {/* ===================================================
            THREE COLUMNS
        ==================================================== */}

        <section className="energy-three-columns">


          {/* BATTERIES */}

          <div
            ref={addReveal}
            className="energy-panel energy-reveal"
          >

            <div className="energy-panel-heading">

              <span />

              <h3>
                OUR SOLAR LITHIUM BATTERIES
              </h3>

            </div>


            <div className="energy-spec-list">

              <SpecItem
                icon={<FaBatteryFull />}
                title="HIGH ENERGY DENSITY"
                description="More power in less space."
              />

              <SpecItem
                icon={<FaChartLine />}
                title="LONG LIFE CYCLE"
                description="Built to last with superior cycle life."
              />

              <SpecItem
                icon={<FaShieldAlt />}
                title="SMART BMS PROTECTION"
                description="Advanced Battery Management System for maximum safety."
              />

              <SpecItem
                icon={<FaBolt />}
                title="FAST CHARGING"
                description="Quick recharge, more efficiency."
              />

              <SpecItem
                icon={<FaLeaf />}
                title="ZERO MAINTENANCE"
                description="Hassle-free performance for years."
              />

            </div>

          </div>


          {/* IDEAL FOR */}

          <div
            ref={addReveal}
            className="energy-panel energy-reveal"
          >

            <div className="energy-panel-heading">

              <span />

              <h3>
                IDEAL FOR
              </h3>

            </div>


            <div className="energy-application-grid">

              <ApplicationCard
                image={homesImage}
                icon={<FaHome />}
                title="HOMES"
              />

              <ApplicationCard
                image={solarSystemsImage}
                icon={<FaSolarPanel />}
                title="SOLAR SYSTEMS"
              />

              <ApplicationCard
                image={commercialImage}
                icon={<FaBuilding />}
                title="COMMERCIAL"
              />

              <ApplicationCard
                image={industrialImage}
                icon={<FaWarehouse />}
                title="INDUSTRIAL"
              />

            </div>

          </div>


          {/* WHY CHOOSE */}

          <div
            ref={addReveal}
            className="energy-panel energy-reveal"
          >

            <div className="energy-panel-heading">

              <span />

              <h3>
                WHY CHOOSE ASHRAFIA ENERGY?
              </h3>

            </div>


            <div className="energy-why-list">

              <WhyItem text="Latest Lithium Technology" />

              <WhyItem text="Premium Grade Cells" />

              <WhyItem text="High Safety & Reliability" />

              <WhyItem text="Excellent Depth of Discharge" />

              <WhyItem text="Scalable Energy Solutions" />

              <WhyItem text="Local Support & After Sales Service" />

            </div>

          </div>

        </section>


        {/* ===================================================
            STRENGTHS
        ==================================================== */}

        <section
          ref={addReveal}
          className="energy-strengths energy-reveal"
        >

          <div className="energy-section-title">

            <span />

            <h2>
              OUR STRENGTHS
            </h2>

            <span />

          </div>


          <div className="energy-strength-grid">

            <StrengthItem
              icon={<FaIndustry />}
              title="ADVANCED MANUFACTURING FACILITY"
            />

            <StrengthItem
              icon={<FaCogs />}
              title="RIGOROUS QUALITY CONTROL & TESTING"
            />

            <StrengthItem
              icon={<FaUsers />}
              title="EXPERT ENGINEERING TEAM"
            />

            <StrengthItem
              icon={<FaShieldAlt />}
              title="RELIABLE & SECURE PERFORMANCE"
            />

            <StrengthItem
              icon={<FaGlobe />}
              title="NATIONWIDE SERVICE & SUPPORT"
            />

          </div>

        </section>


        {/* ===================================================
            ADVANCED FEATURES
        ==================================================== */}

        <section
          ref={addReveal}
          className="energy-advanced-features energy-reveal"
        >

          <AdvancedFeature
            icon={<FaBatteryFull />}
            title="HIGH CAPACITY LITHIUM CELLS"
          />

          <AdvancedFeature
            icon={<FaShieldAlt />}
            title="MULTI-LAYER SAFETY PROTECTION"
          />

          <AdvancedFeature
            icon={<FaTemperatureHigh />}
            title="WIDE TEMPERATURE TOLERANCE"
          />

          <AdvancedFeature
            icon={<FaMobileAlt />}
            title="SMART MONITORING & CONNECTIVITY"
          />

          <AdvancedFeature
            icon={<FaRecycle />}
            title="ECO FRIENDLY & SUSTAINABLE"
          />

        </section>


        {/* ===================================================
            MANUFACTURING
        ==================================================== */}

        <section
          ref={addReveal}
          className="energy-manufacturing energy-reveal"
        >

          <div className="energy-manufacturing-image">

            <img
              src={factoryImage}
              alt="Ashrafia Energy manufacturing facility"
            />

            <div className="energy-manufacturing-image-overlay" />

          </div>


          <div className="energy-manufacturing-content">

            <span className="energy-section-label">
              ADVANCED TECHNOLOGY
            </span>

            <h2>

              ENGINEERED FOR

              <br />

              <strong>
                RELIABLE POWER.
              </strong>

            </h2>

            <p>
              Every Ashrafia Energy storage solution is designed
              around safety, reliability, efficiency and long-term
              performance.
            </p>


            <div className="energy-manufacturing-points">

              <div>

                <FaAward />

                <span>
                  Premium Grade Cells
                </span>

              </div>


              <div>

                <FaFlask />

                <span>
                  Advanced Testing
                </span>

              </div>


              <div>

                <FaChargingStation />

                <span>
                  High Performance
                </span>

              </div>


              <div>

                <FaTools />

                <span>
                  Expert Engineering
                </span>

              </div>

            </div>

          </div>

        </section>


        {/* ===================================================
            FINAL CTA
        ==================================================== */}

        <section
          ref={addReveal}
          className="energy-final-banner energy-reveal"
        >

          <div>

            <span>
              ASHRAFIA ENERGY
            </span>

            <h2>

              POWERING TODAY.

              <br />

              SUSTAINING TOMORROW.

            </h2>

            <p>
              Smart. Reliable. Sustainable Energy Storage.
            </p>

          </div>


          <div className="energy-final-icons">

            <div>

              <FaBolt />

              <span>
                POWERING
                <br />
                TODAY
              </span>

            </div>


            <div>

              <FaLeaf />

              <span>
                SUSTAINING
                <br />
                TOMORROW
              </span>

            </div>


            <div>

              <FaSun />

              <span>
                ENERGIZING
                <br />
                GENERATIONS
              </span>

            </div>

          </div>

        </section>

      </main>


      {/* =====================================================
          FOOTER
      ====================================================== */}

      <footer className="energy-footer">

        <div className="energy-footer-number">
          13
        </div>


        <div className="energy-footer-company">

          <strong>
            ASHRAF GROUP OF INDUSTRIES
          </strong>

          <span>
            Building Legacy. Powering the Future.
          </span>

        </div>


        <div className="energy-footer-right">

          <span>
            POWERING TODAY
          </span>

          <span>
            SUSTAINING TOMORROW
          </span>

          <span>
            ENERGIZING GENERATIONS
          </span>

        </div>

      </footer>


      {/* =====================================================
          CSS
      ====================================================== */}

      <style>{`

        * {
          box-sizing: border-box;
        }


        html {
          scroll-behavior: smooth;
        }


        body {
          margin: 0;
          padding: 0;
          background: #ffffff;
        }


        .energy-page {
          width: 100%;
          min-height: 100vh;
          background: #ffffff;
          color: #17202a;
          font-family:
            Arial,
            Helvetica,
            sans-serif;
          overflow-x: hidden;
        }


        /* =====================================================
           TOP NAVIGATION
        ====================================================== */

        .energy-top-nav {
          width: 100%;
          min-height: 70px;
          padding: 12px 5%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: rgba(255,255,255,.97);
          border-bottom: 1px solid #e6e6e6;
          position: sticky;
          top: 0;
          z-index: 1000;
          backdrop-filter: blur(12px);
        }


        .energy-back-btn {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          padding: 11px 18px;
          border: 1px solid #c99532;
          background: #ffffff;
          color: #182536;
          border-radius: 4px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          transition: .3s ease;
        }


        .energy-back-btn:hover {
          background: #c99532;
          color: #ffffff;
          transform: translateX(5px);
        }


        .energy-nav-title {
          color: #a77922;
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 3px;
        }


        /* =====================================================
           HERO
        ====================================================== */

        .energy-hero {
          position: relative;
          min-height: 780px;
          overflow: hidden;
          background: #ffffff;
        }


        .energy-hero-background {
          position: absolute;
          inset: 0;
          z-index: 0;
        }


        .energy-hero-background img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: .14;
          filter:
            saturate(.75)
            contrast(1.05);
          transform: scale(1.04);
          animation:
            energyHeroZoom 15s ease-in-out infinite alternate;
        }


        .energy-hero-overlay {
          position: absolute;
          inset: 0;
          background:
            linear-gradient(
              90deg,
              rgba(255,255,255,.99) 0%,
              rgba(255,255,255,.96) 38%,
              rgba(255,255,255,.82) 70%,
              rgba(255,255,255,.90) 100%
            );
        }


        .energy-hero-content {
          position: relative;
          z-index: 2;
          width: min(1450px, 100%);
          min-height: 690px;
          margin: auto;
          padding: 55px 6vw 100px;
          display: grid;
          grid-template-columns:
            minmax(0, 1fr)
            minmax(400px, .95fr);
          column-gap: 60px;
          align-items: center;
        }


        /* =====================================================
           LOGO
        ====================================================== */

        .energy-logo-area {
          position: absolute;
          top: 28px;
          left: 50%;
          transform: translateX(-50%);
          text-align: center;
          width: min(600px, 80%);
        }


        .energy-logo {
          max-width: 370px;
          max-height: 100px;
          width: auto;
          height: auto;
          object-fit: contain;
        }


        .energy-logo-line {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 16px;
          margin-top: 3px;
        }


        .energy-logo-line span {
          width: 70px;
          height: 2px;
          background: #c99532;
        }


        .energy-logo-line strong {
          color: #a77922;
          letter-spacing: 7px;
          font-size: 19px;
        }


        .energy-logo-area p {
          margin: 8px 0 0;
          font-size: 10px;
          letter-spacing: 4px;
          color: #68737d;
        }


        /* =====================================================
           HERO TEXT
        ====================================================== */

        .energy-hero-text {
          padding-top: 115px;
          max-width: 680px;
        }


        .energy-small-label {
          display: inline-block;
          color: #a77922;
          font-size: 12px;
          letter-spacing: 3px;
          font-weight: 800;
          margin-bottom: 14px;
        }


        .energy-hero-text h1 {
          margin: 0;
          color: #172536;
          font-size: clamp(42px, 4.5vw, 72px);
          line-height: .98;
          font-weight: 900;
          letter-spacing: -2px;
        }


        .energy-hero-text h2 {
          margin: 14px 0 22px;
          color: #b17d20;
          font-size: clamp(27px, 3vw, 46px);
          line-height: 1;
        }


        .energy-hero-text p {
          color: #5c6873;
          font-size: 15px;
          line-height: 1.7;
          max-width: 650px;
          margin: 0 0 14px;
        }


        /* =====================================================
           MINI FEATURES
        ====================================================== */

        .energy-feature-mini-grid {
          margin-top: 28px;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 10px;
        }


        .energy-feature-mini {
          min-height: 100px;
          border: 1px solid #e4d3ad;
          background: rgba(255,255,255,.94);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          gap: 9px;
          padding: 10px 6px;
          text-align: center;
          border-radius: 5px;
          box-shadow:
            0 8px 25px rgba(32,45,60,.07);
          transition: .35s ease;
        }


        .energy-feature-mini:hover {
          transform: translateY(-7px);
          border-color: #c99532;
          box-shadow:
            0 16px 35px rgba(32,45,60,.13);
        }


        .energy-feature-mini svg {
          color: #b17d20;
          font-size: 25px;
        }


        .energy-feature-mini span {
          color: #33404b;
          font-size: 9px;
          line-height: 1.25;
          font-weight: 800;
        }


        /* =====================================================
           HERO PRODUCTS
        ====================================================== */

        .energy-hero-products {
          padding-top: 125px;
          display: flex;
          flex-direction: column;
          gap: 18px;
        }


        .energy-house-image {
          height: 245px;
          border-radius: 8px;
          overflow: hidden;
          border: 1px solid #ddc38e;
          background: #ffffff;
          box-shadow:
            0 20px 45px rgba(30,45,60,.14);
        }


        .energy-house-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: .7s ease;
        }


        .energy-house-image:hover img {
          transform: scale(1.07);
        }


        .energy-battery-showcase {
          display: grid;
          grid-template-columns: 1fr 1.25fr 1fr;
          gap: 10px;
          align-items: end;
        }


        .energy-battery-card {
          height: 220px;
          background: #ffffff;
          border: 1px solid #e5e5e5;
          border-radius: 8px;
          overflow: hidden;
          box-shadow:
            0 15px 35px rgba(30,45,60,.12);
          transition:
            transform .45s ease,
            box-shadow .45s ease;
        }


        .energy-battery-card:hover {
          transform: translateY(-10px);
          box-shadow:
            0 25px 50px rgba(30,45,60,.18);
        }


        .energy-battery-main {
          height: 280px;
          border-color: #d6b56c;
        }


        .energy-battery-card img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          display: block;
        }


        /* =====================================================
           TECHNOLOGY STRIP
        ====================================================== */

        .energy-tech-strip {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          z-index: 4;
          padding: 20px;
          background: #172536;
          border-top: 3px solid #c99532;
          text-align: center;
        }


        .energy-tech-strip strong {
          display: block;
          color: #ffffff;
          font-size: 21px;
          letter-spacing: 2px;
        }


        .energy-tech-strip span {
          display: block;
          margin-top: 5px;
          color: #d7b15d;
          font-size: 12px;
          letter-spacing: 1px;
        }


        /* =====================================================
           CONTENT
        ====================================================== */

        .energy-content {
          width: 100%;
          background: #ffffff;
        }


        /* =====================================================
           THREE COLUMNS
        ====================================================== */

        .energy-three-columns {
          width: min(1450px, 100%);
          margin: auto;
          padding: 60px 6vw;
          display: grid;
          grid-template-columns:
            repeat(3, minmax(0, 1fr));
          gap: 22px;
        }


        .energy-panel {
          background: #ffffff;
          border: 1px solid #e5e5e5;
          border-top: 3px solid #c99532;
          border-radius: 7px;
          padding: 24px;
          min-height: 430px;
          transition:
            transform .4s ease,
            border-color .4s ease,
            box-shadow .4s ease;
          box-shadow:
            0 10px 35px rgba(32,45,60,.06);
        }


        .energy-panel:hover {
          transform: translateY(-7px);
          border-color: #d4b36d;
          box-shadow:
            0 20px 45px rgba(32,45,60,.11);
        }


        .energy-panel-heading {
          display: flex;
          align-items: center;
          gap: 10px;
          padding-bottom: 14px;
          margin-bottom: 20px;
          border-bottom: 1px solid #ededed;
        }


        .energy-panel-heading > span {
          width: 5px;
          height: 26px;
          background: #c99532;
          border-radius: 3px;
        }


        .energy-panel-heading h3 {
          margin: 0;
          color: #263746;
          font-size: 16px;
          letter-spacing: 1px;
        }


        /* =====================================================
           SPEC LIST
        ====================================================== */

        .energy-spec-list {
          display: flex;
          flex-direction: column;
          gap: 18px;
        }


        .energy-spec-item {
          display: flex;
          gap: 14px;
          align-items: flex-start;
          padding: 4px 0;
          transition: .3s ease;
        }


        .energy-spec-item:hover {
          transform: translateX(7px);
        }


        .energy-spec-icon {
          min-width: 39px;
          width: 39px;
          height: 39px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: #c99532;
          color: #ffffff;
          font-size: 17px;
        }


        .energy-spec-item h4 {
          margin: 0 0 4px;
          font-size: 12px;
          color: #273846;
          letter-spacing: .6px;
        }


        .energy-spec-item p {
          margin: 0;
          color: #71808d;
          font-size: 11px;
          line-height: 1.5;
        }


        /* =====================================================
           APPLICATION
        ====================================================== */

        .energy-application-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }


        .energy-application-card {
          position: relative;
          height: 145px;
          overflow: hidden;
          border-radius: 7px;
          border: 1px solid #e3d4b3;
          cursor: pointer;
        }


        .energy-application-card img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition:
            transform .6s ease;
        }


        .energy-application-card:hover img {
          transform: scale(1.12);
        }


        .energy-application-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: flex-end;
          justify-content: center;
          padding: 13px 5px;
          background:
            linear-gradient(
              transparent 25%,
              rgba(15,25,35,.78)
            );
          color: #ffffff;
          font-size: 11px;
          font-weight: 800;
          gap: 7px;
        }


        .energy-application-overlay svg {
          color: #e0b75c;
        }


        /* =====================================================
           WHY
        ====================================================== */

        .energy-why-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }


        .energy-why-item {
          display: flex;
          align-items: center;
          gap: 11px;
          padding: 12px;
          border-radius: 5px;
          background: #fafafa;
          border: 1px solid #ededed;
          transition: .3s ease;
        }


        .energy-why-item:hover {
          transform: translateX(7px);
          border-color: #c99532;
          background: #fffdf8;
        }


        .energy-check {
          flex-shrink: 0;
          width: 21px;
          height: 21px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: #c99532;
          color: #ffffff;
          font-size: 11px;
        }


        .energy-why-item span:last-child {
          color: #45535f;
          font-size: 11px;
        }


        /* =====================================================
           STRENGTHS
        ====================================================== */

        .energy-strengths {
          width: min(1450px, 100%);
          margin: auto;
          padding: 15px 6vw 50px;
        }


        .energy-section-title {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-bottom: 25px;
        }


        .energy-section-title span {
          height: 1px;
          flex: 1;
          background:
            linear-gradient(
              90deg,
              transparent,
              #c99532
            );
        }


        .energy-section-title span:last-child {
          background:
            linear-gradient(
              90deg,
              #c99532,
              transparent
            );
        }


        .energy-section-title h2 {
          margin: 0;
          color: #a77922;
          font-size: 20px;
          letter-spacing: 3px;
          white-space: nowrap;
        }


        .energy-strength-grid {
          display: grid;
          grid-template-columns:
            repeat(5, minmax(0, 1fr));
          border-top: 1px solid #e0e0e0;
          border-bottom: 1px solid #e0e0e0;
        }


        .energy-strength-item {
          min-height: 155px;
          padding: 25px 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          text-align: center;
          gap: 15px;
          border-right: 1px solid #e5e5e5;
          transition: .35s ease;
        }


        .energy-strength-item:last-child {
          border-right: none;
        }


        .energy-strength-item:hover {
          background: #fffaf0;
          transform: translateY(-5px);
        }


        .energy-strength-item svg {
          font-size: 36px;
          color: #c99532;
        }


        .energy-strength-item span {
          font-size: 10px;
          font-weight: 800;
          line-height: 1.4;
          color: #44515d;
        }


        /* =====================================================
           ADVANCED FEATURES
        ====================================================== */

        .energy-advanced-features {
          width: 100%;
          display: grid;
          grid-template-columns:
            repeat(5, minmax(0, 1fr));
          background: #f8f8f6;
          border-top: 1px solid #e2d4b5;
          border-bottom: 1px solid #e2d4b5;
        }


        .energy-advanced-feature {
          min-height: 145px;
          padding: 22px 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          text-align: center;
          gap: 12px;
          border-right: 1px solid #e5e1d8;
          transition: .35s ease;
        }


        .energy-advanced-feature:last-child {
          border-right: none;
        }


        .energy-advanced-feature:hover {
          background: #fffaf0;
        }


        .energy-advanced-feature svg {
          color: #c99532;
          font-size: 34px;
        }


        .energy-advanced-feature span {
          font-size: 10px;
          color: #35434f;
          font-weight: 800;
          line-height: 1.4;
        }


        /* =====================================================
           MANUFACTURING
        ====================================================== */

        .energy-manufacturing {
          width: min(1450px, 100%);
          margin: auto;
          padding: 75px 6vw;
          display: grid;
          grid-template-columns: 1.05fr .95fr;
          gap: 55px;
          align-items: center;
        }


        .energy-manufacturing-image {
          height: 420px;
          position: relative;
          overflow: hidden;
          border-radius: 10px;
          border: 1px solid #dcc18b;
          box-shadow:
            0 20px 50px rgba(25,40,55,.12);
        }


        .energy-manufacturing-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform .8s ease;
        }


        .energy-manufacturing:hover
        .energy-manufacturing-image img {
          transform: scale(1.05);
        }


        .energy-manufacturing-image-overlay {
          position: absolute;
          inset: 0;
          background:
            linear-gradient(
              90deg,
              rgba(255,255,255,.05),
              rgba(20,30,40,.28)
            );
        }


        .energy-section-label {
          color: #a77922;
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 3px;
        }


        .energy-manufacturing-content h2 {
          margin: 12px 0 20px;
          color: #233342;
          font-size: clamp(32px, 4vw, 58px);
          line-height: 1;
        }


        .energy-manufacturing-content h2 strong {
          color: #b17d20;
        }


        .energy-manufacturing-content > p {
          color: #65727d;
          line-height: 1.7;
          max-width: 600px;
          font-size: 14px;
        }


        .energy-manufacturing-points {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-top: 25px;
        }


        .energy-manufacturing-points div {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px;
          border: 1px solid #eadfc7;
          background: #fffdf8;
          transition: .3s ease;
        }


        .energy-manufacturing-points div:hover {
          transform: translateY(-5px);
          border-color: #c99532;
          box-shadow:
            0 10px 25px rgba(30,45,60,.08);
        }


        .energy-manufacturing-points svg {
          color: #c99532;
          font-size: 20px;
        }


        .energy-manufacturing-points span {
          color: #46545f;
          font-size: 11px;
          font-weight: 700;
        }


        /* =====================================================
           FINAL BANNER
        ====================================================== */

        .energy-final-banner {
          width: min(1450px, 100%);
          margin: 0 auto 55px;
          padding: 50px 6vw;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 40px;
          background:
            linear-gradient(
              110deg,
              #172536,
              #263847
            );
          border-top: 3px solid #c99532;
          border-bottom: 3px solid #c99532;
          box-shadow:
            0 15px 40px rgba(20,35,50,.12);
        }


        .energy-final-banner > div:first-child span {
          color: #d8b15c;
          letter-spacing: 3px;
          font-size: 12px;
          font-weight: 800;
        }


        .energy-final-banner h2 {
          margin: 12px 0;
          color: #ffffff;
          font-size: clamp(30px, 4vw, 55px);
          line-height: 1;
        }


        .energy-final-banner p {
          margin: 0;
          color: #c9d1d8;
        }


        .energy-final-icons {
          display: flex;
          gap: 35px;
        }


        .energy-final-icons div {
          display: flex;
          align-items: center;
          flex-direction: column;
          gap: 9px;
          text-align: center;
          color: #ffffff;
          font-size: 9px;
          font-weight: 800;
          line-height: 1.4;
        }


        .energy-final-icons svg {
          font-size: 31px;
          color: #d5ae54;
        }


        /* =====================================================
           FOOTER
        ====================================================== */

        .energy-footer {
          width: 100%;
          min-height: 105px;
          padding: 18px 6vw;
          display: flex;
          align-items: center;
          gap: 25px;
          background: #f7f7f5;
          color: #1b2b3b;
          border-top: 4px solid #c99532;
        }


        .energy-footer-number {
          background: #c99532;
          color: #ffffff;
          font-size: 38px;
          line-height: 1;
          padding: 14px 23px;
          font-family: Georgia, serif;
        }


        .energy-footer-company {
          display: flex;
          flex-direction: column;
          gap: 5px;
          flex: 1;
        }


        .energy-footer-company strong {
          font-size: 17px;
          letter-spacing: .7px;
        }


        .energy-footer-company span {
          color: #a77922;
          font-size: 12px;
        }


        .energy-footer-right {
          display: flex;
          align-items: center;
          gap: 25px;
        }


        .energy-footer-right span {
          padding-left: 20px;
          border-left: 1px solid #c9c9c9;
          font-size: 9px;
          font-weight: 800;
          color: #4d5a65;
        }


        /* =====================================================
           REVEAL
        ====================================================== */

        .energy-reveal {
          opacity: 0;
          transform: translateY(45px);
          transition:
            opacity .8s ease,
            transform .8s ease;
        }


        .energy-reveal.energy-visible {
          opacity: 1;
          transform: translateY(0);
        }


        @keyframes energyHeroZoom {

          from {
            transform: scale(1.04);
          }

          to {
            transform: scale(1.11);
          }

        }


        /* =====================================================
           TABLET
        ====================================================== */

        @media (max-width: 1050px) {

          .energy-hero-content {
            grid-template-columns: 1fr;
            padding-top: 145px;
            gap: 35px;
          }


          .energy-logo-area {
            top: 25px;
          }


          .energy-hero-text {
            padding-top: 20px;
            max-width: 800px;
          }


          .energy-hero-products {
            padding-top: 0;
          }


          .energy-three-columns {
            grid-template-columns: 1fr 1fr;
          }


          .energy-three-columns
          .energy-panel:last-child {
            grid-column: 1 / -1;
          }


          .energy-strength-grid {
            grid-template-columns: repeat(3, 1fr);
          }


          .energy-strength-item:nth-child(3) {
            border-right: none;
          }


          .energy-advanced-features {
            grid-template-columns: repeat(3, 1fr);
          }


          .energy-advanced-feature:nth-child(3) {
            border-right: none;
          }


          .energy-manufacturing {
            grid-template-columns: 1fr;
          }


          .energy-manufacturing-image {
            height: 360px;
          }


          .energy-footer-right {
            display: none;
          }

        }


        /* =====================================================
           MOBILE
        ====================================================== */

        @media (max-width: 700px) {

          .energy-top-nav {
            min-height: 60px;
            padding: 10px 15px;
          }


          .energy-back-btn {
            font-size: 11px;
            padding: 9px 12px;
          }


          .energy-nav-title {
            font-size: 9px;
            letter-spacing: 2px;
          }


          .energy-hero {
            min-height: auto;
          }


          .energy-hero-content {
            padding:
              125px
              18px
              85px;
            display: flex;
            flex-direction: column;
          }


          .energy-logo-area {
            top: 22px;
            width: 92%;
          }


          .energy-logo {
            max-width: 240px;
            max-height: 65px;
          }


          .energy-logo-line {
            gap: 9px;
          }


          .energy-logo-line span {
            width: 40px;
          }


          .energy-logo-line strong {
            font-size: 13px;
            letter-spacing: 4px;
          }


          .energy-logo-area p {
            font-size: 7px;
            letter-spacing: 2px;
          }


          .energy-hero-text {
            padding-top: 0;
          }


          .energy-hero-text h1 {
            font-size: 39px;
            letter-spacing: -1px;
          }


          .energy-hero-text h2 {
            font-size: 27px;
          }


          .energy-hero-text p {
            font-size: 13px;
          }


          .energy-feature-mini-grid {
            grid-template-columns: 1fr 1fr;
          }


          .energy-feature-mini {
            min-height: 85px;
          }


          .energy-house-image {
            height: 190px;
          }


          .energy-battery-showcase {
            grid-template-columns: 1fr 1fr;
          }


          .energy-battery-card {
            height: 180px;
          }


          .energy-battery-main {
            height: 215px;
            grid-column: span 2;
            order: -1;
          }


          .energy-tech-strip {
            padding: 15px 12px;
          }


          .energy-tech-strip strong {
            font-size: 14px;
          }


          .energy-tech-strip span {
            font-size: 9px;
          }


          .energy-three-columns {
            grid-template-columns: 1fr;
            padding: 35px 15px;
          }


          .energy-three-columns
          .energy-panel:last-child {
            grid-column: auto;
          }


          .energy-panel {
            min-height: auto;
            padding: 18px;
          }


          .energy-panel-heading h3 {
            font-size: 13px;
          }


          .energy-application-card {
            height: 125px;
          }


          .energy-strengths {
            padding:
              10px
              15px
              30px;
          }


          .energy-section-title {
            gap: 10px;
          }


          .energy-section-title h2 {
            font-size: 15px;
            letter-spacing: 2px;
          }


          .energy-strength-grid {
            grid-template-columns: 1fr 1fr;
          }


          .energy-strength-item {
            min-height: 125px;
          }


          .energy-strength-item:nth-child(2) {
            border-right: none;
          }


          .energy-strength-item:nth-child(3) {
            border-right: 1px solid #e5e5e5;
          }


          .energy-strength-item:nth-child(4) {
            border-right: none;
          }


          .energy-strength-item svg {
            font-size: 28px;
          }


          .energy-advanced-features {
            grid-template-columns: 1fr 1fr;
          }


          .energy-advanced-feature {
            min-height: 120px;
          }


          .energy-advanced-feature:nth-child(2),
          .energy-advanced-feature:nth-child(4) {
            border-right: none;
          }


          .energy-advanced-feature:nth-child(3) {
            border-right: 1px solid #e5e1d8;
          }


          .energy-manufacturing {
            padding:
              45px
              15px;
            gap: 30px;
          }


          .energy-manufacturing-image {
            height: 260px;
          }


          .energy-manufacturing-content h2 {
            font-size: 34px;
          }


          .energy-manufacturing-points {
            grid-template-columns: 1fr;
          }


          .energy-final-banner {
            margin-bottom: 30px;
            padding: 35px 20px;
            flex-direction: column;
            align-items: flex-start;
          }


          .energy-final-icons {
            width: 100%;
            justify-content: space-between;
            gap: 10px;
          }


          .energy-footer {
            padding: 15px;
            gap: 15px;
          }


          .energy-footer-number {
            font-size: 26px;
            padding: 10px 15px;
          }


          .energy-footer-company strong {
            font-size: 12px;
          }


          .energy-footer-company span {
            font-size: 9px;
          }

        }


        /* =====================================================
           SMALL MOBILE
        ====================================================== */

        @media (max-width: 420px) {

          .energy-nav-title {
            display: none;
          }


          .energy-hero-text h1 {
            font-size: 34px;
          }


          .energy-hero-text h2 {
            font-size: 24px;
          }


          .energy-feature-mini-grid {
            gap: 6px;
          }


          .energy-feature-mini {
            padding: 7px 3px;
          }


          .energy-feature-mini span {
            font-size: 7px;
          }


          .energy-battery-card {
            height: 145px;
          }


          .energy-battery-main {
            height: 185px;
          }


          .energy-application-grid {
            gap: 7px;
          }


          .energy-application-card {
            height: 110px;
          }


          .energy-strength-item {
            min-height: 115px;
            padding: 15px 7px;
          }


          .energy-strength-item span {
            font-size: 8px;
          }


          .energy-advanced-feature {
            min-height: 105px;
            padding: 15px 5px;
          }


          .energy-advanced-feature svg {
            font-size: 27px;
          }


          .energy-advanced-feature span {
            font-size: 8px;
          }


          .energy-footer {
            align-items: flex-start;
          }

        }

      `}</style>

    </div>

  );
}


/* ============================================================
   COMPONENTS
============================================================ */

function SpecItem({
  icon,
  title,
  description
}) {

  return (

    <div className="energy-spec-item">

      <div className="energy-spec-icon">
        {icon}
      </div>

      <div>

        <h4>
          {title}
        </h4>

        <p>
          {description}
        </p>

      </div>

    </div>

  );

}


function ApplicationCard({
  image,
  icon,
  title
}) {

  return (

    <div className="energy-application-card">

      <img
        src={image}
        alt={title}
      />

      <div className="energy-application-overlay">

        {icon}

        <span>
          {title}
        </span>

      </div>

    </div>

  );

}


function WhyItem({
  text
}) {

  return (

    <div className="energy-why-item">

      <div className="energy-check">
        <FaCheck />
      </div>

      <span>
        {text}
      </span>

    </div>

  );

}


function StrengthItem({
  icon,
  title
}) {

  return (

    <div className="energy-strength-item">

      {icon}

      <span>
        {title}
      </span>

    </div>

  );

}


function AdvancedFeature({
  icon,
  title
}) {

  return (

    <div className="energy-advanced-feature">

      {icon}

      <span>
        {title}
      </span>

    </div>

  );

}